//Node Modules
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";

//Pages

//BinaryForge Components
import logo from "../../assets/img/steeper-logo-en-GB.svg";

//3rd Party Components
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { classNames } from "primereact/utils";

//Atoms
import { toastAtom } from "../../atoms/toast";

//Helpers
import { nav } from "../../config/navigation";

//Other

function VerifyAttribute() {
	const { t } = useTranslation();
	const navigate = useNavigate();

	// Recoil State
	const [toast, setToast] = useRecoilState(toastAtom);

	// Local State
	const [user, setUser] = useState();

	//React Hook Form
	const defaultValues = {
		code: "",
		username: "",
		password: "",
	};
	const {
		control,
		formState: { errors, isSubmitting },
		handleSubmit,
	} = useForm({
		defaultValues,
		mode: "onTouched",
		reValidateMode: "onChange",
	});

	useEffect(() => {
		const getCurrentUser = async () => {
			try {
				const currentUser = await Auth.currentAuthenticatedUser();
				setUser(currentUser);
			} catch (err) {
				console.warn("No Current User");
			}
		};

		getCurrentUser();
	}, []);

	// Form Error Message
	const getFormErrorMessage = (name) => {
		return errors[name] && <span className="font-error font-small">{errors[name].message}</span>;
	};

	// Login Form Submit
	const doVerifyAttribute = async (data) => {
		try {
			if (user) {
				await Auth.verifyCurrentUserAttributeSubmit("email", data.code);
				await Auth.currentAuthenticatedUser({ bypassCache: true });
			} else {
				const newUser = await Auth.signIn(data.username, data.password);
				await Auth.verifyUserAttributeSubmit(newUser, "email", data.code);
				await Auth.signOut();
			}

			setToast({
				...toast,
				severity: "success",
				summary: t("profile.email.toast.successSummary"),
				detail: t("profile.email.toast.successDetail"),
			});

			navigate(nav.home);
		} catch (err) {
			setToast({
				...toast,
				severity: "error",
				summary: t("verifyAttribute.toast.errorSummary"),
				detail: t("verifyAttribute.toast.errorDetail", { error: err.message }),
			});
		}
	};

	return (
		<main className="appMain">
			<div className="appMain-content">
				<img src={logo} alt="Logo" className="logo marginBottomSmall" />
				<div className="appName marginBottom-xlarge text-center">
					<span className="font-large font-lighter text-uppercase">{t("common.appName")}</span>
				</div>
				<form onSubmit={handleSubmit(doVerifyAttribute)}>
					<div className="formFieldsWrapper">
						{!user && (
							<>
								<div className="formField">
									<label htmlFor="username">{t("login.username.label")}</label>
									<Controller
										name="username"
										control={control}
										rules={{ required: t("login.username.required") }}
										render={({ field, fieldState }) => (
											<InputText
												id={field.username}
												{...field}
												className={classNames({ "p-error": fieldState.error })}
											/>
										)}
									/>
									{getFormErrorMessage("username")}
								</div>
								<div className="formField">
									<label htmlFor="password">{t("login.password.label")}</label>
									<Controller
										name="password"
										control={control}
										rules={{ required: t("login.password.required") }}
										render={({ field, fieldState }) => (
											<Password
												id={field.password}
												{...field}
												feedback={false}
												toggleMask={true}
												className={classNames({ "p-error": fieldState.error })}
											/>
										)}
									/>
									{getFormErrorMessage("password")}
								</div>
							</>
						)}
						<div className="formField">
							<label htmlFor="code">{t("profile.email.title")}</label>
							<Controller
								name="code"
								control={control}
								rules={{ required: t("common.form.required") }}
								render={({ field, fieldState }) => (
									<InputText
										id={field.code}
										{...field}
										className={classNames({ "p-error": fieldState.error })}
									/>
								)}
							/>
							{getFormErrorMessage("code")}
						</div>
					</div>

					<Button
						label={isSubmitting ? t("verifyAttribute.submitting") : t("verifyAttribute.submit")}
						type="submit"
						className="feature"
						disabled={isSubmitting}
					/>
				</form>
			</div>
		</main>
	);
}

export default VerifyAttribute;
